/**
 * CSS custom property tokens for the Ringg widget.
 *
 * Consumers can override any token from outside the Shadow DOM:
 *
 *   ringg-widget {
 *     --ringg-primary: #ff6600;
 *     --ringg-font-family: "Inter", sans-serif;
 *   }
 *
 * Internal components read these via var(--ringg-xxx, fallback).
 *
 * Default values match the CDN widget theme exactly (widget-theme.tsx).
 */

import { css } from "lit";

/** Default token values — injected at the :host level of ringg-widget. */
export const hostTokens = css`
  :host {
    /* ─── Colors (CDN defaults) ───────────────── */
    --ringg-primary: #313335;
    --ringg-primary-text: #ffffff;
    --ringg-bg: #fafafa;
    --ringg-surface: #f3f3f5;
    --ringg-text: #212121;
    --ringg-muted: #11111180;
    --ringg-border: #e5e5e5;
    --ringg-error: #ef4444;
    --ringg-success: #10b981;

    /* ─── Typography ──────────────────────────── */
    --ringg-font-family: inherit;
    --ringg-font-size-xs: 0.75rem;
    --ringg-font-size-sm: 0.875rem;
    --ringg-font-size-base: 1rem;
    --ringg-font-size-lg: 1.125rem;
    --ringg-font-size-xl: 1.25rem;

    /* ─── Spacing ─────────────────────────────── */
    --ringg-radius: 20px;
    --ringg-radius-sm: 0.5rem;
    --ringg-radius-xl: 0.75rem;
    --ringg-radius-full: 9999px;
    --ringg-button-radius: 12px;

    /* ─── Sizing ──────────────────────────────── */
    --ringg-widget-width: 360px;
    --ringg-widget-max-height: 96dvh;
    --ringg-trigger-size: 56px;

    /* ─── Shadows ─────────────────────────────── */
    --ringg-shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    --ringg-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    --ringg-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    /* ─── Transitions ─────────────────────────── */
    --ringg-transition: 200ms ease;

    font-family: var(--ringg-font-family);
    color: var(--ringg-text);
  }
`;

/** Shared utility styles used across components. */
export const sharedStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }

  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
  }

  a {
    color: var(--ringg-primary);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;
